import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { getContent } from '@/services/api';
import { makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';

import Placeholder from '../../components/Placeholder';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import { TABLET_P } from '../../constants';

import ResultSection from './ResultSection/ResultSection';
import { PAGE_WIDTH } from '../../styles/variables.style';
import SalonLocatorWithLines from './WithLines/WithLines';

import copy from './data/copy';
import metadata from './data/metadata';
import prefectures from './data/prefectures';
import products from './data/products';
import sitemapInfo from './data/sitemapInfo';
import lines from './data/lines';

const parsUrlParameter = paramString => {
  if (!paramString) return null;

  const paramArray = paramString.slice(1).split('&');

  const parameter = paramArray.reduce((prev, param) => {
    const keyValue = param.split('=');
    const key = decodeURIComponent(keyValue[0]);
    const value = decodeURIComponent(keyValue[1]);

    const result = prev;
    result[key] = value;
    return result;
  }, {});

  return parameter;
};

const Page = styled.section`
  margin: auto;
  max-width: ${PAGE_WIDTH}px;
  width: 100%;
`;

const Title = styled.div`
  margin-top: 20px;
  margin-bottom: 15px;
  padding: 0 15px;

  @media (min-width: 600px) {
    font-size: 26px;
    text-align: center;
    text-transform: uppercase;
    margin-top: 35px;
  }
`;

/**
 * 「サロン検索」画面
 */
class SalonLocator extends React.Component {
  static defaultProps = {
    copy: {},
    products: [],
    prefectures: [],
  };

  static propTypes = {
    copy: PropTypes.shape(),
    locale: PropTypes.string.isRequired,
    // サイトマップ情報 (パンくずリストに必要な情報を含む)
    sitemapInfo: PropTypes.shape({}).isRequired,
    metadata: PropTypes.shape().isRequired,
    products: PropTypes.arrayOf(PropTypes.shape()),
    prefectures: PropTypes.arrayOf(PropTypes.shape()),
    location: PropTypes.shape({}).isRequired,
  };

  state = {
    address: undefined,
    coords: undefined,
    focus: undefined,
    salons: undefined,
    lines: [],
    param: [],
    // SEO 対策に必要なメタデータ
    siteOptionsMetaData: {},
  };

  componentWillMount() {
    const { location, metadata } = this.props;

    this.setState({ siteOptionsMetaData: metadata });

    // パラメータ取得
    const getParam = parsUrlParameter(location.search);
    if (getParam) {
      this.setState({ param: getParam });
    }
  }

  componentDidMount = () => {
    const { locale } = this.props;
    this.setState({ lines: lines[locale] || [] });
  };

  setFocus = focus => {
    this.setState({ focus });
  };

  /**
   * ラインナップを設定する
   */
  getLineLabels = (lines, products) => {
    const labelProducts = products;
    const labelProductsChildren = _.reduce(
      labelProducts,
      (acc, item, key) => (key === 'children' ? [...acc, ...item] : acc),
      []
    );
    const productCategoryLabels = labelProductsChildren.reduce(
      (acc, value) => ({ ...acc, [value.key]: value.label }),
      {}
    );

    const productLabels = this.getLineUpItems(labelProductsChildren).reduce((acc, product) => {
      // TODO: product.key にスペースが空いているので、key 名を変更するか、slug に置き換えることを検討した方が良さそう。
      // TODO: プロパティ名の空白や記号は、有用な識別子を必要としないブラケット記法では参照可能だが、ドット法では参照出来ないリスクがあるため、空白と記号を避ける必要あり。
      // TODO: product.key が取れているものと取れていないものがあるので確認する。
      const productKey = product.slug === 'tgm-hair-scalp-care' ? 'THE GROOMING' : product.key;
      return { ...acc, [productKey]: product.name || product.label };
    }, {});

    const lineLabels = lines.map(data => {
      const categoryLabel = productCategoryLabels[data.lineFieldId.slice(6)];
      return {
        lineFieldId: data.lineFieldId,
        lineFieldLabel: categoryLabel,
        lines: data.lines.map(key => ({
          label: productLabels[key],
          key,
        })),
      };
    });

    // カテゴリー「THE HAIR CARE」を追加する
    const hairCareItems = _.find(lineLabels, { lineFieldId: 'lines_HairCare' });
    if (labelProducts !== undefined) {
      const menuChild = _.find(labelProducts.children, { key: 'HairCare' });
      const labelTheHairCare =
        !!(menuChild && _.get(menuChild, 'children')) && _.find(menuChild.children, { key: 'THE HAIR CARE' });

      if (labelTheHairCare && hairCareItems && hairCareItems.hasOwnProperty.call(hairCareItems, 'lines')) {
        // ADENOVITALの前にTHE HAIR CAREを追加
        const index = hairCareItems.lines.findIndex(line => line.key === 'ADENOVITAL');
        hairCareItems.lines.splice(index, 0, {
          label: productLabels[labelTheHairCare.key],
          key: labelTheHairCare.key,
        });
      }
    }

    return lineLabels;
  };

  /**
   * カテゴリの子要素を収集する (表示用)
   */
  getLineUpItems = products =>
    products.reduce((accumulator, { children }) => {
      if (children && children.length > 0) {
        const hairCareSeries = _.find(children, { key: 'THE HAIR CARE' });
        if (hairCareSeries) {
          const sublimic = _.find(children, { key: 'SUBLIMIC' });
          return [
            ...accumulator,
            // sublimicだけデータ構造が違うので別途追加
            {
              slug: sublimic.key,
              key: sublimic.key,
              name: sublimic.label,
            },
            // sublimic SPA EXPERTだけデータ構造が違うので別途追加
            {
              slug: sublimic.key2,
              key: sublimic.key2,
              name: sublimic.key2Label,
            },
            // THE HAIR CARE 別途追加
            {
              slug: hairCareSeries.key,
              key: hairCareSeries.key,
              name: hairCareSeries.label,
            },
            ...hairCareSeries.children,
          ];
        }
        return [...accumulator, ...children];
      }
      return accumulator;
    }, []);

  searchByAddressWithLine = (address, lineFilter, callback) => {
    const { locale } = this.props;
    const encodedAddress = encodeURIComponent(address);
    const params = { contentType: 'salon', address: encodedAddress, locale };
    if (lineFilter !== null) {
      params.lineFilter = lineFilter;
    }
    getContent(params).then(({ coords, salons }) => {
      this.setState({ address, coords, salons, focus: undefined }, () => {
        if (typeof callback === 'function') callback();
      });
    });
  };

  searchByLocationWithLine = (lat, lon, lineFilter, callback) => {
    const { locale } = this.props;
    const params = { contentType: 'salon', lat, lon, locale };
    if (lineFilter !== null) {
      params.lineFilter = lineFilter;
    }
    getContent(params).then(({ salons }) => {
      this.setState({ address: undefined, coords: { lat, lon }, salons, focus: undefined }, () => {
        if (typeof callback === 'function') callback();
      });
    });
  };

  render() {
    const { address, coords, focus, salons, lines, siteOptionsMetaData, param } = this.state;
    const { copy, prefectures, products, locale, sitemapInfo } = this.props;
    const newLinesData = this.getLineLabels(lines, products);
    const searched = salons !== undefined;

    return (
      <Page>
        {_.get(siteOptionsMetaData, 'metaData') && (
          <Helmet
            title={_.get(siteOptionsMetaData.metaData, 'title')}
            link={[
              makeCanonicalTagObj(locale, '/salon-locator'),
              ...makeHreflangTagArr(siteOptionsMetaData.metaData.region, '/salon-locator'),
            ]}
            meta={[
              {
                name: 'description',
                content: _.get(siteOptionsMetaData.metaData, 'description'),
              },
              {
                name: 'keyword',
                content: _.get(siteOptionsMetaData.metaData, 'keyword'),
              },
              // TODO: OGP タグは一旦保留 (2020年01月07日)
              // {
              //   property: 'og:url',
              //   content: _.get(siteOptionsMetaData.metaData, 'url'),
              // },
              // {
              //   property: 'og:type',
              //   content: _.get(siteOptionsMetaData.metaData, 'type'),
              // },
              // {
              //   property: 'og:title',
              //   content: _.get(siteOptionsMetaData.metaData, 'title'),
              // },
              // {
              //   property: 'og:image',
              //   content: _.get(siteOptionsMetaData.metaData, 'image.file.url'),
              // },
              // {
              //   property: 'og:description',
              //   content: _.get(siteOptionsMetaData.metaData, 'description'),
              // },
              // {
              //   property: 'og:site_name',
              //   content: _.get(siteOptionsMetaData.metaData, 'siteName'),
              // },
            ]}
          />
        )}
        <Title>{copy.find_a_salon || <Placeholder h="30px" w="200px" />}</Title>
        {/* {locale === 'ja_jp' ? ( */}
        <SalonLocatorWithLines
          copy={copy}
          newLinesData={newLinesData || []}
          searchByAddress={this.searchByAddressWithLine}
          searchByLocation={this.searchByLocationWithLine}
          prefectures={prefectures}
          searched={searched}
          searchHandle={this.searchByAddressJp}
          param={param}
          products={products}
        />
        {searched && (
          <ResultSection
            address={address}
            copy={copy}
            coords={coords}
            focus={focus}
            salons={salons}
            setFocus={this.setFocus}
            products={products}
          />
        )}
        {!(locale && sitemapInfo && _.get(sitemapInfo, 'label')) ? (
          <Placeholder w="200px" h="24px" m={window.innerWidth > TABLET_P ? '0 0 20px 40px' : '0 0 20px 20px'} />
        ) : (
          <BreadCrumbMenu
            currentPath={`https://www.shiseido-professional.com/${locale}/salon-locator`}
            items={[
              {
                name: 'TOP',
                path: `/${locale}/`,
              },
              {
                name: sitemapInfo.label,
                path: null,
              },
            ]}
          />
        )}
      </Page>
    );
  }
}

const mapState = (state, props) => {
  const { locale } = props.match.params;
  return {
    copy: copy[locale] || {},
    metadata: metadata[locale] || {},
    prefectures,
    locale,
    products: products[locale] || [],
    sitemapInfo: sitemapInfo[locale] || {},
  };
};

export default connect(mapState)(SalonLocator);



// WEBPACK FOOTER //
// ./src/pages/SalonLocator/SalonLocator.js